<template lang="pug">
    //- The offer reserves room for a typical brief, so its buttons stay still when Carlie's
    //- handoff details settle; only a long summary or more than two rows of chips grows it.
    .carlie-offer-brief
        template(v-if="isLoading")
            v-skeleton-loader.brief-skeleton(type="text, heading" boilerplate)

        template(v-else-if="headline.text || chips.length > 0 || summary")
            .brief-topic.text-uppercase(v-if="topic") {{ topic }}
            .brief-headline(v-if="headline.text") {{ headline.text }}
            .brief-chips(v-if="chips.length > 0")
                v-chip.brief-chip.mr-1.mt-1(
                    v-for="(chip, index) in chips"
                    :key="index"
                    small
                    outlined
                    color="var(--dark-blue-30)"
                    text-color="rgba(0, 0, 0, 0.87)"
                )
                    v-icon(left small :color="chip.color") {{ chip.icon }}
                    span.brief-chip-label {{ chip.label }}
            .brief-summary.text-body-2.text--secondary(v-if="summary") {{ summary }}
        .brief-empty.text-body-2.text--secondary(v-else)
            img.brief-empty-logo.mr-2(:src="carlieLogo" alt="Carlie")
            | {{ emptyText }}
</template>

<script lang="ts">
import Vue from 'vue';

// Assets
import carlieLogo from '@/components/carlie/carlie_logo.png';

// Helpers
import { carlieAgentFlags, carlieHandoffSummary, carlieHeadline } from '@/helpers/carlie';
import { formatTel } from '@/helpers/general';

export default Vue.extend({
    data() {
        return {
            carlieLogo,
        }
    },
    methods: {},
    computed: {
        // How the caller feels first, then what a supervisor configured to stand out, the call's
        // other topics, what the caller went through with Carlie, and scheduling details. Sentiment
        // and topics come from the fast analysis, so they are usually all an offer has; escalation,
        // alerts, and outcomes only show when the full analysis finished first.
        chips(): Array<{ color: string, icon: string, label: string }> {
            const feelings = [];
            // A positive sentiment chip on most calls would be noise, so only a negative one shows.
            if (this.analysis?.sentiment?.is_flagged) {
                feelings.push({ color: "var(--error)", icon: "mdi-emoticon-sad-outline", label: "Negative sentiment" });
            }
            if (this.analysis?.escalation?.is_flagged) {
                feelings.push({ color: "var(--error)", icon: "mdi-account-alert", label: "Escalated customer" });
            }
            const alerts = (this.analysis?.alerts || []).map((alert) => ({
                color: "var(--error)",
                icon: "mdi-bell-alert-outline",
                label: alert.name || alert.purpose,
            }));
            const topics = (this.analysis?.secondary_topics || []).map((topic) => ({
                color: "var(--carlie-purple)",
                icon: "mdi-tag-outline",
                label: topic.label,
            }));
            // --warning is too light against white to meet icon contrast.
            const flags = carlieAgentFlags(this.analysis).map((flag) => ({ ...flag, color: "orange darken-3" }));
            const details = [];
            if (this.handoff?.preferred_callback_window) {
                details.push({ color: "var(--carlie-purple)", icon: "mdi-clock-outline", label: this.handoff.preferred_callback_window });
            }
            // The server only sets callback_number when Carlie collected a number other than the one calling.
            if (this.handoff?.callback_number) {
                details.push({ color: "var(--carlie-purple)", icon: "mdi-phone-return", label: `Call back ${formatTel(this.handoff.callback_number)}` });
            }
            return [...feelings, ...alerts, ...topics, ...flags, ...details].filter((chip) => !!chip.label);
        },
        headline(): { fromIntent: boolean, text: string } {
            return carlieHeadline(this.analysis, this.handoff);
        },
        // Carlie's session summary can be her intent again, sometimes behind a "Caller wants to:"
        // label, and the intent is already the headline.
        summary(): string {
            const summary = (carlieHandoffSummary(this.analysis) || this.handoff?.summary || "").trim();
            const normalize = (value: string) => value.replace(/^caller wants to:\s*/i, "").replace(/[.\s]+$/, "").toLowerCase();
            return normalize(summary) === normalize(this.headline.text) ? "" : summary;
        },
        // Carlie's intent is a bare phrase such as "Tire rotation appointment", so without a topic
        // the label says whose it is.
        topic(): string {
            if (this.analysis?.primary_topic?.label) {
                return this.analysis.primary_topic.label;
            }
            return this.headline.fromIntent ? "Caller wants" : "";
        },
    },
    components: {},
    props: {
        analysis: {
            type: Object,
            default: null,
        },
        emptyText: {
            type: String,
            default: "Carlie's notes will be in the Carlie tab after you accept.",
        },
        handoff: {
            type: Object,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    watch: {},
    created() {},
    destroyed() {},
    mounted() {},
})
</script>

<style lang="scss" scoped>
    .carlie-offer-brief {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 104px;
        overflow: hidden;
        padding: 12px 16px;
        border-left: 3px solid var(--carlie-purple);
        border-radius: 4px;
        background-color: var(--carlie-purple-10);
    }

    .brief-skeleton ::v-deep .v-skeleton-loader__text {
        width: 25%;
        margin-bottom: 12px;
    }

    .brief-topic {
        margin-bottom: 2px;
        font-size: 0.6875rem;
        font-weight: 500;
        letter-spacing: 0.08em;
        color: rgba(0, 0, 0, 0.6);
    }

    .brief-headline {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        font-size: 1.0625rem;
        font-weight: 700;
        line-height: 1.4;
        color: rgba(0, 0, 0, 0.87);
    }

    .brief-chips {
        display: flex;
        flex-wrap: wrap;
        margin-top: 4px;
    }

    .brief-chips ::v-deep .v-chip {
        flex-shrink: 0;
        background-color: var(--white) !important;
    }

    // Only a chip too long for a row of its own is cut.
    .brief-chips ::v-deep .brief-chip {
        max-width: 100%;

        .v-chip__content {
            min-width: 0;
        }
    }

    .brief-chip-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .brief-summary {
        margin-top: 8px;
    }

    .brief-empty {
        display: flex;
        align-items: center;
    }

    .brief-empty-logo {
        height: 20px;
        width: 20px;
    }
</style>
